import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useUploadWizard } from '../../../contexts/UploadWizardContext';
import { CreativeAssignmentMode } from '../../../types/upload';
import { uploadService } from '../../../services/upload';
import { licensingAPI } from '../../../services/api';
import ProgressTracker from '../ProgressTracker';

interface UploadResult {
  fileId: string;
  fileName: string;
  success: boolean;
  error?: string;
  adRecord?: any;
}

const ReviewUploadStep2: React.FC = () => {
  const { state, getCreativeGroups, getTagMetadata } = useUploadWizard();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResult[]>([]);
  const [uploadProgress, setUploadProgress] = useState<any[]>([]);

  const startUpload = async () => {
    setIsUploading(true);
    setUploadResults([]);
    setUploadProgress([]);

    const results: UploadResult[] = [];
    const progressItems: any[] = [];

    // Initialize progress tracking
    state.files.forEach(file => {
      const metadata = state.individualMetadata.get(file.id);
      if (metadata) {
        progressItems.push({
          sessionId: `session_${file.id}`,
          filename: metadata.filename || file.name,
          progress: 0,
          status: 'uploading',
          uploadedBytes: 0,
          totalBytes: file.size,
        });
      }
    });
    setUploadProgress([...progressItems]);

    // Upload files sequentially
    for (let i = 0; i < state.files.length; i++) {
      const file = state.files[i];
      const metadata = state.individualMetadata.get(file.id);
      
      if (!metadata) {
        results.push({
          fileId: file.id,
          fileName: file.name,
          success: false,
          error: 'Missing metadata'
        });
        continue;
      }

      try {
        console.log(`Starting upload for file: ${file.name}`);
        
        // Get tag metadata for this file's creative number
        const tagData = getTagMetadata(metadata.creative_number);
        
        // Create enhanced metadata with tag information
        const enhancedMetadata = {
          ...metadata,
          tags: tagData || undefined
        };
        
        const uploadResult = await uploadService.uploadFile(file, enhancedMetadata, {
          onProgress: (progress) => {
            setUploadProgress(prev => 
              prev.map(item => 
                item.sessionId === `session_${file.id}` 
                  ? { ...item, progress: progress.percentage, uploadedBytes: progress.loaded, totalBytes: progress.total }
                  : item
              )
            );
          },
          onStart: () => {
            console.log(`Upload started for ${file.name}`);
          },
          onComplete: (adRecord) => {
            console.log(`Upload completed for ${file.name}`, adRecord);
            setUploadProgress(prev => 
              prev.map(item => 
                item.sessionId === `session_${file.id}` 
                  ? { ...item, status: 'completed', progress: 100 }
                  : item
              )
            );
          },
          onError: (error) => {
            console.error(`Upload error for ${file.name}:`, error);
            setUploadProgress(prev => 
              prev.map(item => 
                item.sessionId === `session_${file.id}` 
                  ? { ...item, status: 'error' }
                  : item
              )
            );
          }
        });

        results.push({
          fileId: file.id,
          fileName: file.name,
          success: uploadResult.success,
          error: uploadResult.error,
          adRecord: uploadResult.adRecord
        });

      } catch (error: any) {
        console.error(`Upload failed for ${file.name}:`, error);
        results.push({
          fileId: file.id,
          fileName: file.name,
          success: false,
          error: error.message || 'Upload failed'
        });
      }
    }

    // Link song licenses to successfully uploaded ads
    for (const result of results) {
      if (!result.success || !result.adRecord?.ad_id) continue;
      const file = state.files.find(f => f.id === result.fileId);
      if (!file) continue;
      const metadata = state.individualMetadata.get(file.id);
      if (!metadata) continue;
      const creativeNumber = metadata.creative_number;

      const tagData = getTagMetadata(creativeNumber);
      const licenseIds = tagData?.licensedSongIds || [];
      for (const licenseId of licenseIds) {
        try {
          await licensingAPI.link(licenseId, 'ad', result.adRecord.ad_id);
        } catch (e) {
          console.warn(`Failed to link license ${licenseId} to ad ${result.adRecord.ad_id}:`, e);
        }
      }
    }

    // Sync actor licenses for the production: link new, unlink removed
    if (state.productionId) {
      const productionIdStr = String(state.productionId);
      const currentIds = new Set(state.actorLicenseIds);
      const originalIds = new Set(state.originalActorLicenseIds);

      for (const licenseId of currentIds) {
        if (!originalIds.has(licenseId)) {
          try {
            await licensingAPI.link(licenseId, 'production', productionIdStr);
          } catch (e) {
            console.warn(`Failed to link actor license ${licenseId} to production ${productionIdStr}:`, e);
          }
        }
      }

      for (const licenseId of originalIds) {
        if (!currentIds.has(licenseId)) {
          try {
            await licensingAPI.unlink(licenseId, productionIdStr);
          } catch (e) {
            console.warn(`Failed to unlink actor license ${licenseId} from production ${productionIdStr}:`, e);
          }
        }
      }
    }

    setUploadResults(results);
    setIsUploading(false);
  };

  const getSuccessCount = () => uploadResults.filter(r => r.success).length;
  const getFailureCount = () => uploadResults.filter(r => !r.success).length;

  const hasValidationErrors = () => {
    return state.files.some(file => {
      const metadata = state.individualMetadata.get(file.id);
      return !metadata || !metadata.production_id || !metadata.creative_number || 
             !metadata.ratio || !metadata.name_en || !metadata.language || !metadata.created_by;
    });
  };

  const hasAnyValidationErrors = hasValidationErrors();

  const creativeGroups = getCreativeGroups();
  const hasGroups = creativeGroups.size < state.files.length;

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Step 4: Review & Upload
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review your upload configuration and start the upload process.
      </Typography>

      {/* Upload Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upload Summary
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Production ID</Typography>
              <Typography variant="h6">{state.productionId}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Creative Mode</Typography>
              <Typography variant="h6">{state.assignmentMode === CreativeAssignmentMode.EXISTING ? 'Assign to Existing Creatives' : 'Individual Creatives + Grouping'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Files</Typography>
              <Typography variant="h6">{state.files.length}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Creative Groups</Typography>
              <Typography variant="h6">{creativeGroups.size}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Creative Groups Summary */}
      {hasGroups && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Creative Groups
            </Typography>
            {Array.from(creativeGroups.entries()).map(([creativeNumber, group]) => (
              <Box key={creativeNumber} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Creative {creativeNumber}: {group.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Files ({group.files.length}): {group.files.map(({ file }) => file.name).join(', ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Ratios:</strong> {group.ratios.join(', ')}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Validation Errors */}
      {hasAnyValidationErrors && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Configuration Incomplete
          </Typography>
          {hasValidationErrors() && (
            <Typography variant="body2">
              Some files are missing required metadata. Please go back to the previous step and complete all file configurations.
            </Typography>
          )}
        </Alert>
      )}

      {/* Files Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Files to Upload
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File Name</TableCell>
                  <TableCell>Creative #</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Ratio</TableCell>
                  <TableCell>Language</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.files.map((file) => {
                  const metadata = state.individualMetadata.get(file.id);
                  const result = uploadResults.find(r => r.fileId === file.id);
                  
                  let statusChip;
                  if (result) {
                    statusChip = result.success ? (
                      <Chip icon={<CheckCircleIcon />} label="Uploaded" color="success" size="small" />
                    ) : (
                      <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                    );
                  } else if (isUploading) {
                    statusChip = <Chip label="Uploading..." color="primary" size="small" />;
                  } else if (metadata) {
                    statusChip = <Chip icon={<CheckCircleIcon />} label="Ready" color="success" size="small" />;
                  } else {
                    statusChip = <Chip icon={<ErrorIcon />} label="Missing Metadata" color="error" size="small" />;
                  }

                  return (
                    <TableRow key={file.id}>
                      <TableCell>{file.name}</TableCell>
                      <TableCell>{metadata?.creative_number || 'N/A'}</TableCell>
                      <TableCell>{metadata?.name_en || 'N/A'}</TableCell>
                      <TableCell>{metadata?.ratio || 'N/A'}</TableCell>
                      <TableCell>{metadata?.language || 'N/A'}</TableCell>
                      <TableCell>{statusChip}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upload Progress
            </Typography>
            <ProgressTracker
              uploads={uploadProgress}
              onCancel={() => {
                // Handle cancel logic if needed
                console.log('Upload cancelled');
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Upload Results */}
      {uploadResults.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Upload Results
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Alert severity={getFailureCount() === 0 ? "success" : "warning"}>
                <Typography variant="subtitle2">
                  {getSuccessCount()} of {uploadResults.length} files uploaded successfully
                </Typography>
                {getFailureCount() > 0 && (
                  <Typography variant="body2">
                    {getFailureCount()} file(s) failed to upload. Check the details below.
                  </Typography>
                )}
              </Alert>
            </Box>

            {uploadResults.filter(r => !r.success).map(result => (
              <Alert key={result.fileId} severity="error" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{result.fileName}</Typography>
                <Typography variant="body2">{result.error}</Typography>
              </Alert>
            ))}

            {uploadResults.filter(r => r.success).map(result => (
              <Alert key={result.fileId} severity="success" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{result.fileName}</Typography>
                <Typography variant="body2">
                  Successfully uploaded as {result.adRecord?.ad_id}
                </Typography>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upload Action */}
      {!isUploading && uploadResults.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={startUpload}
            disabled={hasAnyValidationErrors}
            sx={{ minWidth: 200, py: 1.5 }}
          >
            Start Upload
          </Button>
          {hasAnyValidationErrors && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Complete all file configurations to enable upload
            </Typography>
          )}
        </Box>
      )}

      {/* Upload Complete Actions */}
      {uploadResults.length > 0 && !isUploading && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Upload Complete!
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              // Reset for new upload
              setUploadResults([]);
              setUploadProgress([]);
            }}
            sx={{ mr: 2 }}
          >
            Upload More Files
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Navigate to productions page or dashboard
              window.location.href = '/productions';
            }}
          >
            View Productions
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ReviewUploadStep2;